import { useState, useEffect, useMemo, useCallback } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from "material-react-table";
import {
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import { IListaCidades } from "../../../../../../../application/models/geo-veles/IListaCidades";
import { useListaCidadesService } from "../../../../../../../use-cases/geo-veles/lista-cidades";
import { useAppSelector } from "../../../../../../hooks/store-hooks";
import { useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles";

// Interface para os dados de Cidade
interface CidadeData {
    id: number;
    nome: string;
    uf: string;
    display: string;
}

// Interface para listas filtradas
interface ListaFiltrada {
    lista: IListaCidades;
    cidadesFiltradas: CidadeData[];
    nomeCorresponde: boolean;
}

// Componente individual para cada lista
const ListaCidadesVirtualTable = ({
    lista,
    cidadesFiltradas,
    mostraTodasCidades
}: {
    lista: IListaCidades;
    cidadesFiltradas: CidadeData[];
    mostraTodasCidades: boolean;
}) => {
    const columns = useMemo<MRT_ColumnDef<CidadeData>[]>(
        () => [
            {
                accessorKey: 'display',
                header: 'Cidade - UF',
                size: 250,
                Cell: ({ cell }) => (
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.875rem'
                        }}
                    >
                        {cell.getValue<string>()}
                    </Typography>
                ),
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns,
        data: cidadesFiltradas,
        enablePagination: false,
        enableRowNumbers: false,
        enableToolbarInternalActions: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        // Configurações de virtualização para performance
        enableRowVirtualization: true,
        enableStickyHeader: true,
        // Ajustes de layout responsivo
        layoutMode: 'grid',
        // Configurações de desempenho
        defaultDisplayColumn: {
            enableResizing: false,
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: '400px',
                minHeight: '200px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                height: '40px',
            },
        },
        muiTableBodyCellProps: {
            sx: {
                padding: '8px 16px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
    });

    const totalCidades = useMemo(() => {
        return cidadesFiltradas.length;
    }, [cidadesFiltradas]);

    return (
        <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexShrink: 0 }}>
                <FormatListBulletedIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                    {lista.Nome || `Lista ${lista.IdVelesGeoListaCidade}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {mostraTodasCidades
                        ? `${totalCidades} cidade(s)`
                        : `${cidadesFiltradas.length} de ${totalCidades} cidade(s)`
                    }
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <MaterialReactTable table={table} />
            </Box>
        </Paper>
    );
};

export const RecorteListasCidadesFilterCreatePreview = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [listasCarregadas, setListasCarregadas] = useState<IListaCidades[]>([]);
    const [loading, setLoading] = useState(true);

    const listasIds = useAppSelector(
        (state) => state.recorteExtracao.createRecorteExtracaoForm.fields.Filtros?.listasCidades
    );

    const { removeCreateRecorteFormFiltros } = useRecortesExtracaoService();
    const { getListaCidadesById } = useListaCidadesService();

    const carregarListas = useCallback(async () => {
        if (!listasIds || listasIds.length === 0) {
            setListasCarregadas([]);
            setLoading(false);
            return;
        }

        const ids = Array.from(new Set(listasIds));

        setLoading(true);
        const listasDetalhadas: IListaCidades[] = [];
        for (const id of ids) {
            try {
                const lista = await getListaCidadesById(id);
                listasDetalhadas.push(lista);
            } catch (error) {
                console.error(`❌ Erro ao carregar lista de cidades com ID ${id}:`, error);
            }
        }

        setListasCarregadas(listasDetalhadas);
        setLoading(false);
    }, [listasIds, getListaCidadesById]);

    useEffect(() => {
        carregarListas();
    }, [carregarListas]);

    const parseCidades = useCallback((cidadesJsonString: string | null | undefined): CidadeData[] => {
        if (!cidadesJsonString) return [];
        try {
            const parsed = JSON.parse(cidadesJsonString);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((item: any, index: number) => ({
                        id: index,
                        nome: (item.Nome || '').toString().trim(),
                        uf: (item.UF || '').toString().trim(),
                        display: `${(item.Nome || '').toString().trim()} - ${(item.UF || '').toString().trim()}`
                    }))
                    .filter(cidade => cidade.nome && cidade.uf);
            }
        } catch (error) {
            console.error("❌ Falha ao fazer parse do campo Cidades:", error);
        }
        return [];
    }, []);

    // Filtra todas as listas baseado no termo de busca geral
    const listasFiltradas = useMemo((): ListaFiltrada[] => {
        if (listasCarregadas.length === 0) return [];

        if (!searchTerm.trim()) {
            // Se não há busca, mostra todas as listas com todas as cidades
            return listasCarregadas.map((lista) => {
                const cidades = parseCidades(lista.Cidades);
                return {
                    lista,
                    cidadesFiltradas: cidades,
                    nomeCorresponde: true,
                };
            });
        }

        const term = searchTerm.toLowerCase();

        return listasCarregadas.map((lista) => {
            const nomeCorresponde = (lista.Nome?.toLowerCase() || '').includes(term);
            const todasCidades = parseCidades(lista.Cidades);

            const cidadesFiltradas = todasCidades.filter(
                (cidade) =>
                    cidade.nome.toLowerCase().includes(term) ||
                    cidade.uf.toLowerCase().includes(term) ||
                    cidade.display.toLowerCase().includes(term)
            );

            return { lista, cidadesFiltradas, nomeCorresponde };
        }).filter((lista) => lista.nomeCorresponde || lista.cidadesFiltradas.length > 0);
    }, [listasCarregadas, searchTerm, parseCidades]);

    // Calcula o layout responsivo baseado na quantidade de listas
    const getGridLayout = () => {
        const count = listasFiltradas.length;
        if (count === 0) return { xs: 12 };
        if (count === 1) return { xs: 12 };
        return { xs: 12, md: 6 }; // Máximo 2 por linha
    };

    const handleRemoveFilter = () => {
        removeCreateRecorteFormFiltros({ filtro: 'listasCidades' });
    };

    return (
        <Grid item container xs={12} spacing={2} mb={2} p={2}>
            <Paper elevation={3} sx={{ padding: 2, width: "100%" }}>
                <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography
                        variant="body1"
                        color="primary"
                        fontWeight="bold"
                        textTransform="uppercase"
                    >
                        Listas de Cidades ({listasCarregadas.length})
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveFilter}
                        size="small"
                    >
                        Remover filtro
                    </Button>
                </Grid>

                {loading ? (
                    <Grid container justifyContent="center" my={2}>
                        <CircularProgress size={24} />
                    </Grid>
                ) : (
                    <>
                        {/* Barra de busca GERAL única */}
                        <TextField
                            size="small"
                            placeholder="Pesquisar por nome da lista, cidade ou UF..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                                ),
                            }}
                        />

                        {/* Grid responsivo com listas lado a lado (máximo 2 por linha) */}
                        <Grid container spacing={2}>
                            {listasFiltradas.length > 0 ? (
                                listasFiltradas.map((listaFiltrada) => (
                                    <Grid item {...getGridLayout()} key={listaFiltrada.lista.IdVelesGeoListaCidade.toString()}>
                                        <ListaCidadesVirtualTable
                                            lista={listaFiltrada.lista}
                                            cidadesFiltradas={listaFiltrada.cidadesFiltradas}
                                            mostraTodasCidades={!searchTerm.trim()}
                                        />
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body1" color="text.secondary">
                                            Nenhuma lista ou cidade encontrada para "{searchTerm}"
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}
            </Paper>
        </Grid>
    );
};